import { BreakpointObserver } from '@angular/cdk/layout';
import { ComponentPortal } from '@angular/cdk/portal';
import { UpperCasePipe } from '@angular/common';
import { Component, computed, effect, inject, Injector, OnDestroy, signal, untracked, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';

import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FuseScrollResetDirective } from '@fuse/directives/scroll-reset';
import { KDateFormat } from '@kiwid-app/kiwid-date-typescript';
import {
    BtnComponent,
    EmptyComponent,
    IModalConfig,
    ITabEvent,
    KFormIconComponent,
    KFormInputComponent,
    KLabelComponent,
    ModalService,
    TabBarComponent,
    ToastrService,
} from '@kiwid-app/kiwid-ui-lib-angular';
import { TranslatePipe } from '@ngx-translate/core';
import { DATE_API } from 'app/core/const/date.const';
import { DEFAULT_PAGINATION } from 'app/core/const/pagination.const';
import { EOrderRemakeType, EOrderStatus } from 'app/core/enums/order.enum';
import { ConvertHelper } from 'app/core/helpers/convert.helper';
import { CountrySettingHelper } from 'app/core/helpers/country-setting.helper';
import { formatDate, parseDate, parseDateWithTime } from 'app/core/helpers/date.helper';
import { ErrorHelper } from 'app/core/helpers/error.helper';
import { LabelStatusHelper } from 'app/core/helpers/label-status.helper';
import { isLastPage, Util } from 'app/core/helpers/util';
import { IOrderRating } from 'app/core/models/order/rating';
import { IGetPatient, IPatient } from 'app/core/models/patient/patient';
import { EAclFunction, EAclModule } from 'app/core/permissions/enum/access.enum';
import { hasPermission, KiwidPermissionsService } from 'app/core/permissions/service/kiwid-permissions.service';
import { AuthenticationService } from 'app/core/services/authentication/authentication.service';
import { LocationService } from 'app/core/services/laboratory/location.service';
import { MoneyService } from 'app/core/services/money/money.service';
import { OrderService } from 'app/core/services/order/order.service';
import { PatientService } from 'app/core/services/patient/patient.service';
import { BaseComponent } from 'app/layout/common/base/base.component';
import { ChatComponent } from 'app/modules/production/order/detail/chat/chat.component';
import { ModalCreateRatingComponent } from 'app/modules/production/order/detail/modal-create-rating/modal-create-rating.component';
import { OrderDetailViewService } from 'app/modules/production/order/detail/order-detail-view.service';
import { AvatarModule } from 'ngx-avatars';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { debounceTime, distinctUntilChanged, first, last } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { locale as english } from './i18n/en';
import { locale as spanish } from './i18n/es';
import { locale as portuguese } from './i18n/pt';
import { ModalRatingComponent } from './modal-rating/modal-rating.component';
import { ClinicMessageComponent } from 'app/modules/production/order/detail/clinic-message/clinic-message.component';

@Component({
    selector: 'app-detail',
    imports: [
        RouterLink,
        TranslatePipe,
        BtnComponent,
        KLabelComponent,
        AvatarModule,
        NgxSkeletonLoaderModule,
        NgxTippyModule,
        MatSidenavModule,
        MatIcon,
        RouterOutlet,
        TabBarComponent,
        UpperCasePipe,
        MatMenu,
        MatMenuContent,
        FuseScrollResetDirective,
        MatMenuTrigger,
        MatMenuItem,
        ReactiveFormsModule,
        KFormInputComponent,
        MatProgressSpinner,
        EmptyComponent,
        KFormIconComponent,
    ],
    templateUrl: './detail.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './detail.component.scss',
})
export class DetailComponent extends BaseComponent implements OnDestroy {
    readonly router = inject(Router);
    private readonly orderService = inject(OrderService);
    private readonly locationService = inject(LocationService);
    private readonly toastrService = inject(ToastrService);
    private readonly orderDetailViewService = inject(OrderDetailViewService);
    private readonly labelStatusHelper = inject(LabelStatusHelper);
    private readonly countrySettingHelper = inject(CountrySettingHelper);
    private readonly injector = inject(Injector);
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly kiwidPermissionsService = inject(KiwidPermissionsService);
    private readonly moneyService = inject(MoneyService);
    private readonly modalService = inject(ModalService);
    private readonly title = inject(Title);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly patientService = inject(PatientService);
    private readonly formBuilder = inject(FormBuilder);

    protected readonly hasPermissionFinancial = signal(
        hasPermission({
            routeAccess: [
                {
                    mod: EAclModule.FINANCIAL_BILLING,
                    fns: [EAclFunction.LIST],
                },
            ],
        }),
    );

    private orderId = signal(inject(ActivatedRoute).snapshot.paramMap.get('orderId'));
    private readonly routerParams = toSignal(inject(ActivatedRoute).params);
    private readonly routerSourceType = signal(
        decodeURIComponent(inject(ActivatedRoute)?.snapshot.queryParamMap.get('source') ?? '/production/order'),
    );
    protected readonly backRouterLink = computed(() => this.routerSourceType().split('?')[0]);
    protected readonly backRouterQueryParam = computed(() => Util.getObjectQueryParamsByUrl(this.routerSourceType() ?? ''));
    protected readonly isLoading = signal(true);
    protected readonly order = computed(() => {
        const order = this.orderDetailViewService.order();
        const isOrderFinished = this.isOrderFinished();
        const isCurrentUserIsCreateOrder = this.isCurrentUserIsCreateOrder();

        if (!order) {
            return null;
        }

        const deliveryRoute = order.deliveryRoutes?.at(0) ?? null;

        return {
            ...order,
            table: {
                number: !!order.number ? this.translateKey('MENU.NAME', { number: order.number }) : '-',
                remakeTooltip: !!order.remakeType
                    ? order.remakeType === EOrderRemakeType.REMAKE
                        ? this.translateKey('REMAKE_TYPE.REMAKE')
                        : this.translateKey('REMAKE_TYPE.ADJUSTMENT')
                    : null,
                remakeLabel: !!order.remakeType
                    ? order.remakeType === EOrderRemakeType.REMAKE
                        ? this.translateKey('REMAKE_TYPE.REMAKE_INITIAL')
                        : this.translateKey('REMAKE_TYPE.ADJUSTMENT_INITIAL')
                    : null,
                isPriority: order.isPriority ?? false,
                laboratory: {
                    name: order.location?.name,
                    logo: order.laboratory?.logo?.url,
                },
                customerUser:
                    this.hasPermissionOrderTotal() && !!order.customerUser
                        ? {
                              name: !!order.customerUser ? order.customerUser?.name : '-',
                              avatar: order.customerUser?.avatar?.url,
                          }
                        : null,
                patient: !!order.patient && !!order.patient?.name ? order.patient?.name : '-',
                enableEditPatient:
                    this.hasPermissionOrderUpdate() &&
                    ![EOrderStatus.FINISHED, EOrderStatus.CANCELLED, EOrderStatus.DELIVERED].includes(order.status),
                status: {
                    icon: this.labelStatusHelper.getOrderMatIcon(order.status),
                    type: this.labelStatusHelper.getOrderColor(order.status),
                    text: this.labelStatusHelper.getOrderText(order.status),
                },
                orderDate: !!order.orderDate ? KDateFormat(new Date(order.orderDate), this.translateKey('DATETIME_FORMAT')) : null,
                expectedDate:
                    !!order.expectedDate && ![EOrderStatus.PROOF, EOrderStatus.SUSPENDED].includes(order.status)
                        ? `${KDateFormat(
                              {
                                  value: order.expectedDate,
                                  format: DATE_API,
                              },
                              this.translateKey('DATE_FORMAT'),
                          )} ${!!order.expectedTime ? order.expectedTime : ''}`
                        : null,
                address: !!order.address
                    ? {
                          firstLineAddress: `${order.address.street}, ${order.address.number}`,
                          secondLineAddress: [order.address.complement, order.address.district].filter((item) => !!item).join(', '),
                          thirdLineAddress: [
                              this.countrySettingHelper.getPostalCodeName(order.address.postalCode ?? ''),
                              order.address.city,
                              order.address.state,
                          ]
                              .filter((item) => !!item)
                              .join(', '),
                      }
                    : null,
                rating:
                    (this.hasPermissionOrderTotal() || this.hasPermissionRating()) &&
                    [EOrderStatus.FINISHED, EOrderStatus.DELIVERED].includes(order.status)
                        ? !!order.ratingValue
                            ? { note: order.ratingNote, value: order.ratingValue }
                            : null
                        : null,
                hasCreateRating: !order.ratingValue && isOrderFinished && (isCurrentUserIsCreateOrder || this.hasPermissionOrderTotal()),
                isShowApproval:
                    order.status === EOrderStatus.APPROVAL &&
                    this.hasPermissionFinancial() &&
                    this.kiwidPermissionsService.hasPermission({
                        routeAccess: [
                            {
                                mod: EAclModule.ORDER_APPROVAL,
                                fns: [EAclFunction.CREATE],
                            },
                        ],
                    }),
                isDeliveryConfirmed: order.isDeliveryConfirmed && {
                    text: this.translateKey('TOOLTIP.IS_DELIVERY_CONFIRMED'),
                },
                deliveryRoute: !!deliveryRoute
                    ? {
                          finishedDate: !!deliveryRoute?.finishedDate
                              ? formatDate(parseDateWithTime(deliveryRoute.finishedDate), this.translateKey('DATETIME_FORMAT'))
                              : null,
                          expectedDate: formatDate(parseDate(deliveryRoute.expectedDate), this.translateKey('DATE_FORMAT')),
                          period: this.labelStatusHelper.getDeliveryRoutePeriodText(deliveryRoute.period),
                          trackingNumber: deliveryRoute.trackingNumber,
                          status: {
                              type: this.labelStatusHelper.getDeliveryRouteStatusColor(deliveryRoute.status),
                              text: this.labelStatusHelper.getDeliveryRouteStatusText(deliveryRoute.status),
                          },
                      }
                    : null,
            },
        };
    });
    protected readonly isOrderFinished = computed(() =>
        [EOrderStatus.FINISHED, EOrderStatus.DELIVERED].includes(this.orderDetailViewService.order()?.status),
    );
    protected readonly ratingStars = computed(() => {
        const percentage: string[] = [];
        const rating = this.orderDetailViewService.order()?.ratingValue ?? 0;

        for (let i = 1; i <= 5; i++) {
            percentage.push(rating > i ? '100%' : `${(i - rating > 1 ? 0 : (i - rating - 1) * -1 * 100).toFixed(2)}%`);
        }

        return percentage;
    });
    protected readonly ratingValue = computed(() => {
        return ConvertHelper.convertToMaskMoney(
            this.orderDetailViewService.order()?.ratingValue ?? 0,
            {
                decimalSeparator: this.moneyService.maskMoney.decimalSeparator,
                thousandSeparator: this.moneyService.maskMoney.thousandSeparator,
                prefix: '',
            },
            1,
        );
    });
    protected enableLayoutMobile = signal(true);
    protected readonly tabItems = signal<IInternalItem[]>([]);
    protected readonly items = computed(() => this.tabItems().map((item) => item.text));
    protected readonly selectedTabIndex = signal(0);
    protected readonly selectedTabText = signal('');
    protected readonly getSelectedTabIndex = computed(() => {
        const iTabItem = this.tabItems().findIndex((tab) => tab.text === this.selectedTabText());
        return iTabItem > -1 ? iTabItem : 0;
    });
    protected readonly hasPermissionChat = signal(
        hasPermission({
            routeAccess: [
                {
                    mod: EAclModule.CHAT,
                    fns: [EAclFunction.LIST],
                },
            ],
        }),
    );
    protected readonly hasPermissionMedia = signal(
        hasPermission({
            routeAccess: [
                {
                    mod: EAclModule.ORDER_MEDIA,
                    fns: [EAclFunction.LIST],
                },
            ],
        }),
    );
    protected readonly hasPermissionRating = signal(
        hasPermission({
            routeAccess: [
                {
                    mod: EAclModule.ORDER_RATING,
                    fns: [EAclFunction.LIST],
                },
            ],
        }),
    );
    protected readonly hasPermissionOrderTotal = signal(
        hasPermission({
            routeAccess: [
                {
                    mod: EAclModule.ORDER_TOTAL,
                    fns: [EAclFunction.LIST],
                },
            ],
        }),
    );
    protected readonly hasPermissionOrderUpdate = signal(
        hasPermission({
            routeAccess: [
                {
                    mod: EAclModule.ORDER,
                    fns: [EAclFunction.UPDATE],
                },
            ],
        }),
    );
    protected readonly isLoadingApproval = signal(false);
    protected readonly isCurrentUserIsCreateOrder = computed(
        () => this.orderDetailViewService.order()?.customerUser?.userId === this.authenticationService.user().id,
    );
    public drawer = viewChild('drawer', { read: MatDrawer });
    protected readonly isLoadingPatientFooter = signal(false);
    protected readonly isLoadingPatient = signal(false);
    protected readonly isLoadingSetPatient = signal(false);
    private readonly isLastPage = signal(false);
    private readonly patientQueryParams = signal<IGetPatient>({
        offset: 0,
        limit: DEFAULT_PAGINATION,
        search: '',
    });
    private readonly patientSelect = signal<IPatient[]>([]);
    protected readonly renderPatientSelect = computed(() => {
        const patientSelect = this.patientSelect();
        return patientSelect.map((patient) => {
            return {
                id: patient.id,
                name: patient.kFullName(),
            };
        });
    });
    protected readonly formPatient = signal(
        this.formBuilder.group({
            search: [],
        }),
    );
    protected readonly formPatientSearch = computed(() => this.formPatient().get('search') as FormControl);
    protected readonly trigger = viewChild('trigger', { read: MatMenuTrigger });

    constructor() {
        super();
        this.registerOnTranslate('ORDER_DETAIL', portuguese, english, spanish, false);
        this.registerLayoutMobile();
        this.registerTabItems();
        this.registerForm();

        effect(() => {
            this.routerParams();
            const orderId = this.activatedRoute.snapshot.paramMap.get('orderId');

            untracked(() => {
                if (!!orderId && this.order()?.id !== orderId) {
                    this.orderId.set(orderId);
                    this.isLoading.set(true);
                    this.orderDetailViewService.setOrder(null);
                    this.getData();
                }
            });
        });
    }

    ngOnDestroy() {
        this.orderDetailViewService.clearOrder();
        super.ngOnDestroy();
    }

    private getData(): void {
        this.subscriptions.add(
            this.orderService
                .getOrderDetail({
                    orderId: this.orderId(),
                })
                .pipe(last())
                .subscribe({
                    next: ({ data }) => {
                        this.subscriptions.add(
                            this.locationService
                                .getLocationSettings({
                                    locationId: data.locationId,
                                })
                                .pipe(first())
                                .subscribe({
                                    next: ({ data: dataLocationSettings }) => {
                                        this.orderDetailViewService.locationSettings.set(dataLocationSettings);
                                        this.changeTab();
                                        this.orderDetailViewService.setOrder(data);
                                        this.isLoading.set(false);
                                    },
                                    error: (error) => {
                                        this.isLoading.set(false);
                                        ErrorHelper.setToastrError(this.toastrService, error);
                                    },
                                }),
                        );
                        this.changeTab();
                        this.orderDetailViewService.setOrder(data);
                        this.isLoading.set(false);
                    },
                    error: (error) => {
                        this.isLoading.set(false);
                        ErrorHelper.setToastrError(this.toastrService, error);
                    },
                }),
        );
    }

    private registerTabItems(): void {
        const items: IInternalItem[] = [
            {
                text: this.translateKey('TABS.INFORMATION'),
                link: [`./${EOrderDetailTab.INFORMATION}`],
            },
        ];

        if (this.hasPermissionChat() && !this.enableLayoutMobile() && this.order()?.status !== EOrderStatus.DRAFT) {
            items.push({
                text: this.translateKey('TABS.CHAT'),
                link: [`./${EOrderDetailTab.CHAT}`],
            });
        }

        if (this.hasPermissionMedia()) {
            items.push({
                text: this.translateKey('TABS.FILES'),
                link: [`./${EOrderDetailTab.FILES}`],
            });
        }

        this.tabItems.set(items);
    }

    protected eventTab(event: ITabEvent): void {
        this.selectedTabIndex.set(event.index);
        if (this.selectedTabIndex() >= this.tabItems().length) {
            this.selectedTabIndex.set(0);
        }

        const item = this.tabItems()[this.selectedTabIndex()];
        this.selectedTabText.set(item.text);
        this.router
            .navigate(item.link, {
                relativeTo: this.activatedRoute,
                queryParams: { source: this.routerSourceType },
            })
            .then();
    }

    private isLinkActive(url: [string]): boolean {
        const queryParamsIndex = this.router.url.indexOf('?');
        const baseUrl = queryParamsIndex === -1 ? this.router.url : this.router.url.slice(0, queryParamsIndex);
        return baseUrl.search(url[0]) > -1;
    }

    private changeTab(): void {
        if (!this.tabItems().some((linkItem) => this.isLinkActive(linkItem.link))) {
            this.router
                .navigate(this.tabItems()[0].link, {
                    relativeTo: this.activatedRoute,
                    queryParams: { source: this.routerSourceType },
                })
                .then();
            this.selectedTabText.set(this.tabItems()[0].text);
            return;
        }

        const iTab = this.tabItems().findIndex((linkItem) => this.isLinkActive(linkItem.link));
        if (iTab > -1) {
            this.selectedTabIndex.set(iTab);
            this.selectedTabText.set(this.tabItems()[iTab].text);
        }
    }

    private registerLayoutMobile(): void {
        effect(
            () => {
                const enable = this.enableLayoutMobile();

                untracked(() => {
                    if (!enable) {
                        this.drawer()?.toggle(true)?.then();
                    }
                });
            },
            { injector: this.injector },
        );

        this.subscriptions.add(
            this.breakpointObserver.observe('(max-width: 700px)').subscribe(({ matches }) => {
                this.enableLayoutMobile.set(matches);
            }),
        );
    }

    protected toggleSidebar(): void {
        this.drawer().toggle().then();
    }

    protected sendApproval(): void {
        this.isLoadingApproval.set(true);

        this.subscriptions.add(
            this.orderService.approveOrder(this.orderId()).subscribe({
                next: () => {
                    this.toastrService.show({
                        type: 'success',
                        message: this.translateKey('TOASTR.ORDER_APPROVE.SUCCESS.MESSAGE'),
                    });
                    this.isLoadingApproval.set(false);
                    this.orderDetailViewService.setOrder({
                        ...this.orderDetailViewService.order(),
                        status: EOrderStatus.PENDING,
                    });
                },
                error: (error) => {
                    this.isLoadingApproval.set(false);
                    ErrorHelper.setToastrError(this.toastrService, error);
                },
            }),
        );
    }

    openChat(): void {
        const config: IModalConfig = {
            title: this.translate?.TABS?.CHAT,
            type: 'full-screen',
            backgroundColor: 'var(--kiwid-bg-page)',
            component: new ComponentPortal<ClinicMessageComponent>(ClinicMessageComponent),
        };

        const dialog = this.modalService.openModal(config);

        this.subscriptions.add(
            dialog.componentInstance.instanceObservable<ClinicMessageComponent>().subscribe((data) => {
                data.isMobile.set(true);
            }),
        );

        this.subscriptions.add(
            dialog
                .afterOpened()
                .pipe(last())
                .subscribe(() => {
                    this.subscriptions.add(
                        dialog.componentInstance.buttonEventClick.subscribe((button) => {
                            if (button.id === 2) {
                                dialog.close();
                            }
                        }),
                    );
                }),
        );
    }

    protected openModalCreateRating(): void {
        if (this.order().ratingValue || !this.isOrderFinished() || !(this.isCurrentUserIsCreateOrder() || this.hasPermissionOrderTotal())) {
            return;
        }

        const config: IModalConfig = {
            title: this.translateKey('MODAL.ORDER_RATING.TITLE'),
            type: 'custom',
            width: '560px',
            component: new ComponentPortal<ModalCreateRatingComponent>(ModalCreateRatingComponent),
            buttons: [],
        };

        const dialog = this.modalService.openModal(config);

        this.subscriptions.add(
            dialog.componentInstance.instanceObservable<ModalCreateRatingComponent>().subscribe((data) => {
                data.dialog = signal(dialog);
                data.order = signal(this.order());
            }),
        );

        this.subscriptions.add(
            dialog
                .afterClosed()
                .pipe(first())
                .subscribe((data) => {
                    try {
                        if (!!data) {
                            const result = JSON.parse(data) as IOrderRating;
                            this.orderDetailViewService.setOrder({
                                ...this.orderDetailViewService.order(),
                                ratingValue: result.ratingValue,
                                ratingNote: result.note,
                            });
                        }
                    } catch (e) {
                        console.log(e);
                    }

                    this.title.setTitle(`${environment.titleBase} - ${this.translateKey('TITLE')}`);
                }),
        );
    }

    protected openModalRating(): void {
        const config: IModalConfig = {
            title: this.translateKey('MODAL.ORDER_RATING.TITLE'),
            type: 'custom',
            width: '878px',
            component: new ComponentPortal<ModalRatingComponent>(ModalRatingComponent),
            buttons: [],
        };

        const dialog = this.modalService.openModal(config);

        this.subscriptions.add(
            dialog.componentInstance.instanceObservable<ModalRatingComponent>().subscribe((data) => {
                data.dialog = signal(dialog);
                data.orderId = signal(this.order().id);
            }),
        );

        this.subscriptions.add(
            dialog
                .afterClosed()
                .pipe(first())
                .subscribe(() => {
                    this.title.setTitle(`${environment.titleBase} - ${this.translateKey('TITLE')}`);
                }),
        );
    }

    private registerForm() {
        this.subscriptions.add(
            this.formPatient()
                .get('search')
                .valueChanges.pipe(debounceTime(400), distinctUntilChanged())
                .subscribe((value) => {
                    this.patientQueryParams.set({
                        offset: 0,
                        limit: DEFAULT_PAGINATION,
                        search: value,
                    });
                    this.getPatient(true);
                }),
        );
    }

    protected getPatient(isInit: boolean = false): void {
        this.isLoadingPatientFooter.set(true);

        if (isInit) {
            this.isLoadingPatientFooter.set(false);
            this.isLoadingPatient.set(true);
            this.patientQueryParams.set({
                offset: 0,
                limit: DEFAULT_PAGINATION,
                search: '',
            });
            this.patientSelect.set([]);
        }

        this.subscriptions.add(
            this.patientService
                .getPatients(this.patientQueryParams())
                .pipe(last())
                .subscribe(({ data, meta }) => {
                    this.isLastPage.set(isLastPage(meta));

                    this.patientSelect.update((items) => [...items, ...data.filter((patient) => patient.id !== this.order().patient.patientId)]);

                    this.isLoadingPatient.set(false);
                    this.isLoadingPatientFooter.set(false);
                }),
        );
    }

    protected onPatientScroll(event: Event): void {
        const element = event.target as Element;
        if (element.scrollHeight - element.clientHeight <= element.scrollTop + 10 && !this.isLoadingPatientFooter() && !this.isLastPage()) {
            if (!this.isLoadingPatient() && !this.isLastPage()) {
                this.patientQueryParams.update((prev) => ({
                    ...prev,
                    offset: this.patientSelect().length,
                }));
                this.getPatient();
            }
        }
    }

    protected setPatient(patientId: string): void {
        this.isLoadingSetPatient.set(true);

        this.subscriptions.add(
            this.orderService
                .updatePatient({
                    orderId: this.orderId(),
                    patientId: patientId,
                })
                .pipe(last())
                .subscribe({
                    next: ({ data, message }) => {
                        this.orderDetailViewService.setOrder({
                            ...this.orderDetailViewService.order(),
                            patient: data,
                        });
                        this.toastrService.show({
                            message,
                            type: 'success',
                        });
                        this.trigger()?.closeMenu();
                        this.isLoadingSetPatient.set(false);
                    },
                    error: (error) => {
                        this.trigger()?.closeMenu();
                        this.isLoadingSetPatient.set(false);
                        ErrorHelper.setToastrError(this.toastrService, error);
                    },
                }),
        );
    }
}

export enum EOrderDetailTab {
    INFORMATION = 'information',
    CHAT = 'chat',
    FILES = 'media',
}

interface IInternalItem {
    text: string;
    link: [string];
}
